import { type FC, useState, useEffect, useRef, type ReactNode } from "react";
import type { WebAuth } from "auth0-js";
import { Slides, type SlidesRef } from "@huub-web/ui";
import { useTranslation } from "react-i18next";

import { useAuthConfig } from "../../hooks/useAuthConfig";
import {
  createWebAuth,
  createAuth0Client,
  loginWithPassword,
  loginWithProvider,
  signupWithPassword,
  clearAuth0Storage,
  type AuthResult,
  type Auth0Client,
} from "../../services/auth0";
import { setAuthCookie } from "../../services/auth-cookie";
import { decodeJwtPayload } from "../../services/decode-jwt-payload";
import { Auth0ServiceError } from "../../types/";

import {
  CallbackSlide,
  type EmailVerificationData,
} from "./components/CallbackSlide";
import { EmailVerificationSlide } from "./components/EmailVerificationSlide";
import { ForgotPasswordSlide } from "./components/ForgotPasswordSlide";
import { LoginSlide } from "./components/LoginSlide";
import { RegisterSlide } from "./components/RegisterSlide";
import { VerificationResultSlide } from "./components/VerificationResultSlide";

export type AuthScreensProps = {
  /**
   * The trailing element to show on the slides
   */
  slideTrailing?: ReactNode;
  /**
   * The leading element to show on the slides
   */
  slideLeading?: ReactNode;
  /**
   * The default view to show when the component is mounted
   */
  defaultView?:
    | "login"
    | "register"
    | "callback"
    | "forgotPassword"
    | "loginVerify";
  /**
   * Callback function when loading state changes
   */
  onLoadingChange?: (loading: boolean) => void;
  /**
   * Callback function when authentication is successful
   */
  onSuccess?: () => void;
  /**
   * Whether to show the back button in the forgot password slide
   */
  forgotPasswordBack?: boolean;
  /**
   * Whether the email verification was successful (for loginVerify view)
   */
  verificationSuccess?: boolean;
  /**
   * Message to display on verification result (for loginVerify view)
   */
  verificationMessage?: string | null;
  /**
   * Informational message to display on the login form (e.g., session expired)
   */
  loginMessage?: string | null;
  /**
   * Callback to resend verification email. Receives auth0Id and returns success status.
   * Must be provided by the consuming app since the auth package has no API layer.
   */
  onResendVerificationEmail?: ({
    auth0Id,
  }: {
    auth0Id: string;
  }) => Promise<{ success: boolean }>;
};

/**
 * AuthScreens component for authentication.
 * Contains LoginForm, RegistrationForm, and CallbackSlide with switching capability using Slides.
 */
export const AuthScreens: FC<AuthScreensProps> = ({
  slideTrailing,
  slideLeading,
  forgotPasswordBack,
  defaultView = "register",
  onLoadingChange,
  onSuccess,
  verificationSuccess = false,
  verificationMessage = null,
  loginMessage = null,
  onResendVerificationEmail,
}) => {
  const { t, i18n } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;

  const config = useAuthConfig();

  const [webAuth, setWebAuth] = useState<WebAuth | null>(null);
  const [auth0Client, setAuth0Client] = useState<Auth0Client | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailVerificationData, setEmailVerificationData] =
    useState<EmailVerificationData | null>(null);

  const slidesRef = useRef<SlidesRef>(null);

  useEffect(() => {
    const initAuth0 = async () => {
      try {
        // Initialize WebAuth client for password-based auth
        const webAuthClient = createWebAuth(config.auth0);
        setWebAuth(webAuthClient);

        // Initialize SPA SDK client for social login (authorization code flow with PKCE)
        const spaClient = await createAuth0Client({
          ...config.auth0,
          locale: i18n.language,
        });
        setAuth0Client(spaClient);
      } catch (err) {
        console.error("Failed to initialize Auth0:", err);
        setError(tRef.current("error.loadingLogin"));
      }
    };

    void initAuth0();
  }, [config.auth0, i18n.language]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  /**
   * Verify tokens with the backend and handle the response
   * @param authResult - The authentication result containing tokens
   */
  const verifyTokensWithBackend = async (
    authResult: AuthResult,
  ): Promise<void> => {
    // Decode the id_token to get auth0Id as fallback (sub claim contains the Auth0 user ID)
    const { sub: auth0IdFromToken, email: emailFromToken } = decodeJwtPayload(
      authResult.idToken,
    );

    const response = await fetch(config.api.verifyTokenEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        access_token: authResult.accessToken,
        id_token: authResult.idToken,
        refresh_token: authResult.refreshToken,
      }),
    });

    if (!response.ok) {
      throw new Error(t("error.verificationFailed"));
    }

    const data = (await response.json()) as {
      email_verified?: boolean;
      email?: string;
      auth0_id?: string;
      id_token?: string | null;
    };

    if (data.email_verified === false) {
      setEmailVerificationData({
        email: data.email ?? emailFromToken ?? null,
        auth0Id: data.auth0_id ?? auth0IdFromToken ?? null,
      });
      slidesRef.current?.goTo("emailVerification");
      return;
    }

    // Set the auth cookie for cross-domain sharing with the app
    if (!data.id_token) {
      throw new Error(t("error.noTokenReceived"));
    }

    setAuthCookie({
      idToken: data.id_token,
      name: config.cookie.name,
      domain: config.cookie.domain,
    });
    clearAuth0Storage();
    onSuccess?.();
  };

  /**
   * Handle login with email and password using embedded auth
   * @param email - User's email address
   * @param password - User's password
   */
  const handleLogin = async (email: string, password: string) => {
    if (!webAuth) {
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const authResult = await loginWithPassword(webAuth, email, password);
      await verifyTokensWithBackend(authResult);
    } catch (err: unknown) {
      console.error("Login error:", err);

      if (err instanceof Auth0ServiceError) {
        setError(t(`authErrors.${err.code}`));
      } else {
        const errorMessage =
          err instanceof Error ? err.message : t("error.login");
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle registration with email and password using embedded auth
   * @param email - User's email address
   * @param password - User's password
   */
  const handleRegister = async (email: string, password: string) => {
    if (!webAuth) {
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const authResult = await signupWithPassword(webAuth, email, password);
      await verifyTokensWithBackend(authResult);
    } catch (err: unknown) {
      console.error("Register error:", err);

      if (err instanceof Auth0ServiceError) {
        setError(t(`authErrors.${err.code}`));
      } else {
        const errorMessage =
          err instanceof Error ? err.message : t("error.register");
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const switchToRegister = () => {
    slidesRef.current?.goTo("register");
    setError(null);
  };

  const switchToLogin = () => {
    slidesRef.current?.goTo("login");
    setError(null);
  };

  /**
   * Generic provider login handler using SPA SDK.
   * @param provider - The Auth0 connection name (e.g., 'google-oauth2', 'windowslive')
   */
  const handleProviderLogin = async (provider: string) => {
    if (!auth0Client) {
      return;
    }

    setError(null);

    try {
      await loginWithProvider(auth0Client, provider, i18n.language);
    } catch (err: unknown) {
      console.error("Provider login error:", err);

      if (err instanceof Auth0ServiceError) {
        setError(t(`authErrors.${err.code}`));
      } else {
        const errorMessage =
          err instanceof Error ? err.message : t("error.providerLogin");
        setError(errorMessage);
      }
    }
  };

  /**
   * Generic provider registration handler using SPA SDK.
   * For social providers, login and registration are the same flow.
   * @param provider - The Auth0 connection name (e.g., 'google-oauth2', 'windowslive')
   */
  const handleProviderRegistration = async (provider: string) => {
    if (!auth0Client) {
      return;
    }

    setError(null);

    try {
      await loginWithProvider(auth0Client, provider, i18n.language);
    } catch (err: unknown) {
      console.error("Provider registration error:", err);

      if (err instanceof Auth0ServiceError) {
        setError(t(`authErrors.${err.code}`));
      } else {
        const errorMessage =
          err instanceof Error ? err.message : t("error.providerRegistration");
        setError(errorMessage);
      }
    }
  };

  /**
   * Handler for retrying authentication after a callback error.
   */
  const handleRetry = () => {
    slidesRef.current?.goTo("login");
  };

  return (
    <Slides
      ref={slidesRef}
      initialSlide={defaultView}
      slides={{
        register: (
          <RegisterSlide
            trailing={slideTrailing}
            leading={slideLeading}
            webAuth={webAuth}
            error={error}
            loading={loading}
            onSubmit={handleRegister}
            onSwitchToLogin={switchToLogin}
            onProviderRegistration={handleProviderRegistration}
          />
        ),
        login: (
          <LoginSlide
            trailing={slideTrailing}
            leading={slideLeading}
            webAuth={webAuth}
            error={error}
            infoMessage={loginMessage}
            onSubmit={handleLogin}
            onSwitchToRegister={switchToRegister}
            onProviderLogin={handleProviderLogin}
            onLoadingChange={setLoading}
            onForgotPassword={() => {
              slidesRef.current?.goTo("forgotPassword");
            }}
          />
        ),
        callback: (
          <CallbackSlide
            trailing={slideTrailing}
            leading={slideLeading}
            auth0Client={auth0Client}
            onRetry={handleRetry}
            onLoadingChange={setLoading}
            onSuccess={onSuccess}
            onNeedsEmailVerification={(data) => {
              setEmailVerificationData(data);
              slidesRef.current?.goTo("emailVerification");
            }}
          />
        ),
        emailVerification: (
          <EmailVerificationSlide
            trailing={slideTrailing}
            leading={slideLeading}
            email={emailVerificationData?.email ?? null}
            auth0Id={emailVerificationData?.auth0Id ?? null}
            onBackToLogin={switchToLogin}
            onLoadingChange={setLoading}
            onResendVerificationEmail={onResendVerificationEmail}
          />
        ),
        forgotPassword: (
          <ForgotPasswordSlide
            trailing={slideTrailing}
            leading={slideLeading}
            onBackToLogin={forgotPasswordBack ? switchToLogin : undefined}
            onLoadingChange={setLoading}
          />
        ),
        loginVerify: (
          <VerificationResultSlide
            trailing={slideTrailing}
            leading={slideLeading}
            success={verificationSuccess}
            message={verificationMessage}
            onContinue={() => {
              slidesRef.current?.goTo(
                verificationSuccess || !emailVerificationData
                  ? "login"
                  : "emailVerification",
              );
            }}
          />
        ),
      }}
    />
  );
};
